import { Button } from "@/components/Button/Button";
import { Checkbox } from "@/components/Checkbox/Checkbox";
import { NoSearchResult } from "@/components/EmptyState/NoSearchResult";
import { CollapsedSvg, SortAscendSvg, SortDescendSvg, SortSvg } from "@/components/Icon/Icon";
import { constructSelectRangeOfRows } from "@/components/Table/tableHelpers";
import { COMPLETE_CASE_STATUSES } from "@/constants/common";
import { CASE_TABLE_STATE_NAME } from "@/constants/designTableConstants";
import { clientApi } from "@/store/clientApi";
import { addSelectedId, removeSelectedId, setDetailsItem, setDrawerOpen, setSelectedIds } from "@/store/slices/detailsDrawerSlice";
import {
  invalidate,
  removeDeleted,
  selectCanCollapse,
  selectCanCollapseRow,
  selectChecked,
  selectCollapsible,
  selectQueryPage,
  selectQueryPerPage,
  selectQuerySortField,
  selectQuerySortOrder,
  selectRtkData,
  selectTableChecked,
  selectTableCollapsible,
  setPage,
  setSortField,
  setSortOrder,
  setTableChecked,
  toggleChecked,
  toggleCollapsible
} from "@/store/slices/tableSlice";
import { RootState, store } from "@/store/store";
import { Ordering } from "@/typed/Common";
import { ModelType } from "@/typed/ModelTypes";
import {
  Box,
  IconButton,
  Pagination,
  Paper,
  SvgIcon,
  SxProps,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme
} from "@mui/material";
import Table from "@mui/material/Table";
import React, { forwardRef, Fragment, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";

type MenuOption = {
  event: (event: React.MouseEvent<HTMLElement>) => Promise<void>;
  name: string;
  hideName?: boolean;
  icon?: typeof SvgIcon;
  variant?: "outlined" | "contained" | "text";
};

type Sort = {
  column: string;
  key?: string;
  order: Ordering;
};

type Column = {
  name: string;
  key?: string;
  sortable?: boolean;
  hover?: boolean;
  minWidth?: number;
};

interface DynamicTableProps<T> {
  stateName: string;
  columns: Column[];
  renderEntry: (entry: T) => React.ReactNode[];
  renderCollapsible?: (visible: boolean, entry: T, selected: boolean) => React.JSX.Element | React.JSX.Element[] | null;
  defaultCollapsedState?: boolean;
  checkable: boolean;
  menuOptions?: MenuOption[];
  tableData: T[];
  totalNumEntities: number;
  sx?: SxProps<Theme>;
  onSort?: (sort: Sort | undefined) => void;
  noMatch?: boolean;
  noData?: boolean;
  noDataEmptyStateComponent?: React.ReactElement;
  selectable?: boolean;
  mapId: (entry: T) => string;
  hidePagination?: boolean;
}

interface RowProps<T> {
  entry: T;
  mapId: (entry: T) => string;
  stateName: string;
  checkable: boolean;
  renderEntry: (entry: T) => React.ReactNode[];
  renderCollapsible?: (visible: boolean, entry: T, selected: boolean) => React.JSX.Element | React.JSX.Element[] | null;
  selectable?: boolean;
  selectRows: (id: string, itemType: ModelType) => void;
  columns: Column[];
}

const Collapsible = <T,>({
  entry,
  collapsibleState,
  checkedState,
  renderCollapsible
}: {
  entry: T;
  collapsibleState: boolean;
  checkedState: boolean;
  renderCollapsible?: (state: boolean, entry: T, checked: boolean) => React.ReactNode;
}) => {
  return <>{renderCollapsible?.(collapsibleState, entry, checkedState)}</>;
};

const Row = <T,>({ entry, mapId, stateName, renderCollapsible, selectable, checkable, renderEntry, columns, selectRows }: RowProps<T>) => {
  const { t } = useTranslation();
  const id = mapId(entry);

  const { id: selectedTab } = useParams();
  const location = useLocation();
  const basePath = location.pathname.split("/")[1];

  const canCollapseRow = useSelector((state: RootState) => selectCanCollapseRow(state, stateName, id)) ?? true;
  const canCollapse = useSelector((state: RootState) => selectCanCollapse(state, stateName));
  const collapsibleState = useSelector((state: RootState) => selectCollapsible(state, stateName, id));

  const checkedState = useSelector((state: RootState) => selectChecked(state, stateName, id));

  const status = useSelector(
    (state: RootState) =>
      selectRtkData(state, CASE_TABLE_STATE_NAME, clientApi.endpoints.getCasesApiV1CasesGet.select)?.data?.content?.find(
        (part) => part.id.toString() === id
      )?.status
  );
  const itemType = status !== undefined && COMPLETE_CASE_STATUSES.includes(status) ? "based-model" : "scan";

  const handleClickOnCheckBox = (event: React.MouseEvent<unknown>) => {
    event.stopPropagation();

    if (checkedState) {
      store.dispatch(
        removeSelectedId({
          page: basePath,
          tab: selectedTab,
          selectedId: id
        })
      );
    } else {
      store.dispatch(
        addSelectedId({
          page: basePath,
          tab: selectedTab,
          selectedId: id,
          itemLabel: "combined",
          itemType: itemType
        })
      );
    }
    store.dispatch(toggleChecked({ name: stateName, checked: id }));
  };

  const handleClickOnRow = (event: React.MouseEvent<unknown>) => {
    event.stopPropagation();

    if (event.ctrlKey) {
      if (checkedState) {
        store.dispatch(
          removeSelectedId({
            page: basePath,
            tab: selectedTab,
            selectedId: id
          })
        );
      } else {
        store.dispatch(
          addSelectedId({
            page: basePath,
            tab: selectedTab,
            selectedId: id,
            itemLabel: "combined",
            itemType: itemType
          })
        );
      }
      store.dispatch(toggleChecked({ name: stateName, checked: id }));
      return;
    } else if (event.shiftKey) {
      selectRows(id, itemType);
      return;
    }

    store.dispatch(
      setDetailsItem({
        page: basePath,
        tab: selectedTab,
        selectedId: id,
        itemType: itemType,
        itemLabel: "combined"
      })
    );
    store.dispatch(
      setDrawerOpen({
        value: true
      })
    );
    store.dispatch(
      setTableChecked({
        name: stateName,
        checked: { [id]: true },
        lastChecked: id
      })
    );
  };

  return (
    <Fragment>
      <TableRow
        sx={{
          "&& > * .MuiCheckbox-root": { visibility: checkedState ? "visible" : "hidden" },
          "&&:hover > * .MuiCheckbox-root": { visibility: "visible" },
          "&& > * .hidden-cell": { visibility: "hidden" },
          "&&:hover > * .hidden-cell": { visibility: "visible" },
          ...(selectable && { cursor: "pointer" })
        }}
        className={"entity-row"}
        hover
        // Parent row is highlighted if its id equals the selectedId.
        selected={checkedState}
        onClick={handleClickOnRow}
      >
        {canCollapse && (
          <TableCell padding="checkbox">
            {canCollapseRow && (
              <IconButton
                aria-label={t("common:table.expandRow")}
                size="small"
                onClick={(e: React.SyntheticEvent) => {
                  e.stopPropagation();
                  store.dispatch(toggleCollapsible({ name: stateName, collapsible: id }));
                }}
                sx={{
                  transform: collapsibleState ? "rotate(90deg)" : "none",
                  transition: "0.1s ease-in-out",
                  "& svg": {
                    color: (theme: Theme) => theme.outline.light
                  }
                }}
              >
                <CollapsedSvg color="inherit" />
              </IconButton>
            )}
          </TableCell>
        )}
        {checkable && (
          <TableCell padding="checkbox">
            <Checkbox checked={checkedState} onClick={handleClickOnCheckBox} />
          </TableCell>
        )}
        {renderEntry(entry).map((cell, cellIndex) => (
          <TableCell key={`${columns[cellIndex].name}`} style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
            <div className={columns[cellIndex].hover ? "hidden-cell" : undefined}>{cell}</div>
          </TableCell>
        ))}
      </TableRow>
      {canCollapse && (
        <Collapsible entry={entry} collapsibleState={collapsibleState} checkedState={checkedState} renderCollapsible={renderCollapsible} />
      )}
    </Fragment>
  );
};

const Title = ({
  column,
  index,
  onSort,
  stateName,
  width
}: {
  column: Column;
  index: number;
  stateName: string;
  width: string;
  onSort?: (sort: Sort | undefined) => void;
}) => {
  const sortField = useSelector((state: RootState) => selectQuerySortField(state, stateName));
  const sortOrder = useSelector((state: RootState) => selectQuerySortOrder(state, stateName));

  const handleOnSort = () => {
    let field = undefined;
    let order: Ordering = "asc";

    if (sortField === column.key) {
      if (sortOrder === "asc") {
        field = column.key;
        order = "desc";
      } else {
        field = undefined;
      }
    } else {
      field = column.key ?? "";
      order = "asc";
    }

    if (onSort === undefined) {
      store.dispatch(setSortField({ name: stateName, value: field ?? "" }));
      store.dispatch(setSortOrder({ name: stateName, value: order }));
      return;
    }

    if (field !== undefined) {
      onSort?.({
        column: column.name,
        key: column.key,
        order
      });
    } else {
      onSort?.(undefined);
    }
  };

  return (
    <TableCell
      key={`column-${index}`}
      sx={{
        ...(column.sortable
          ? {
              "&& > * .MuiButtonBase-root": {
                visibility: sortField !== column.key ? "hidden" : "visible"
              },
              "&&:hover > * .MuiButtonBase-root": {
                visibility: "visible"
              }
            }
          : {
              "&& > * .MuiButtonBase-root": {
                visibility: "hidden"
              }
            }),
        width: width
      }}
    >
      <div style={{ visibility: column.hover ? "hidden" : "visible" }}>
        {column.name}
        <IconButton
          sx={{
            height: "24px",
            padding: "0px",
            marginLeft: "4px"
          }}
          id={`sort-by-${column.name}`}
          onClick={handleOnSort}
        >
          {sortField !== column.key ? (
            <SortSvg color="inherit" />
          ) : sortOrder === "asc" ? (
            <SortAscendSvg color="inherit" />
          ) : (
            <SortDescendSvg color="inherit" />
          )}
        </IconButton>
      </div>
    </TableCell>
  );
};

const Header = <T,>({
  stateName,
  checkable,
  columns,
  tableData,
  mapId,
  onSort,
  tableContainerWidth
}: {
  stateName: string;
  checkable: boolean;
  columns: Column[];
  tableData: T[];
  onSort?: (sort: Sort | undefined) => void;
  mapId: (entry: T) => string;
  tableContainerWidth: number;
}) => {
  const { id: selectedTab } = useParams();
  const basePath = useLocation().pathname.split("/")[1];
  const checked = useSelector((state: RootState) => selectTableChecked(state, stateName));
  const canCollapse = useSelector((state: RootState) => selectCanCollapse(state, stateName));
  const numChecked = Object.values(checked).filter((value) => value).length;
  const someChecked = numChecked > 0;
  const allChecked = numChecked === tableData.length && tableData.length !== 0;

  const handleOnClickCheckbox = () => {
    if (allChecked) {
      store.dispatch(
        setTableChecked({
          name: stateName,
          checked: Object.assign(
            {},
            ...Object.entries(checked).map(([key]) => {
              return { [key]: false };
            })
          ),
          lastChecked: undefined
        })
      );
      store.dispatch(
        setSelectedIds({
          page: basePath,
          tab: selectedTab,
          selectedIds: []
        })
      );
    } else {
      store.dispatch(
        setTableChecked({
          name: stateName,
          checked: Object.assign(
            {},
            ...tableData.map((id) => {
              return { [mapId(id)]: true };
            })
          ),
          lastChecked: undefined
        })
      );
      store.dispatch(
        setSelectedIds({
          page: basePath,
          tab: selectedTab,
          selectedIds: tableData.map((id) => mapId(id))
        })
      );
    }
  };

  return (
    <TableHead>
      <TableRow
        sx={{
          "&& > * .MuiCheckbox-root": {
            display: allChecked || someChecked ? "block" : "none"
          },
          "&&:hover > * .MuiCheckbox-root": { display: "block" }
        }}
      >
        {canCollapse && <TableCell padding="checkbox"></TableCell>}
        {checkable && (
          <TableCell padding="checkbox">
            <Checkbox
              id={`table-all-checked`}
              checked={allChecked}
              indeterminate={!allChecked && someChecked}
              onClick={handleOnClickCheckbox}
            />
          </TableCell>
        )}
        {columns.map((column, index) => {
          const minWidth = column.minWidth ?? DEFAULT_MIN_COLUMN_WIDTH_PX;
          const sumMinWidths = columns.reduce((sum, col) => sum + (col.minWidth ?? DEFAULT_MIN_COLUMN_WIDTH_PX), 0);
          return (
            <Title
              key={`header-${index}`}
              column={column}
              index={index}
              onSort={onSort}
              stateName={stateName} // as the table is fixed, width needs to include the min-width calculation
              width={`max(${minWidth}px, ${(minWidth / sumMinWidths) * tableContainerWidth - 49}px)`}
            />
          );
        })}
      </TableRow>
    </TableHead>
  );
};

const TableMenuItem = ({ menu, stateName }: { menu: MenuOption; stateName: string }) => {
  const Icon = menu.icon;
  return (
    <div>
      {menu.hideName && Icon ? (
        <IconButton
          onClick={async (e: React.MouseEvent<HTMLElement>) => {
            await menu.event(e);
            store.dispatch(
              setTableChecked({
                name: stateName,
                checked: {},
                lastChecked: undefined
              })
            );
          }}
          sx={{ height: "2.5em" }}
          aria-label={menu.name}
        >
          <Icon />
        </IconButton>
      ) : (
        <Button
          onClick={async (e: React.MouseEvent<HTMLElement>) => {
            await menu.event(e);
            store.dispatch(
              setTableChecked({
                name: stateName,
                checked: {},
                lastChecked: undefined
              })
            );
          }}
          aria-label={menu.name}
          variant={menu.variant ? menu.variant : "outlined"}
          sx={{ height: "2.5em" }}
        >
          {Icon && <Icon />}
          {menu.name}
        </Button>
      )}
    </div>
  );
};

const DynamicTableUpdates = <T,>({ stateName, tableData, mapId }: { mapId: (entry: T) => string; stateName: string; tableData: T[] }) => {
  const checked = useSelector((state: RootState) => selectTableChecked(state, stateName));
  const collapsible = useSelector((state: RootState) => selectTableCollapsible(state, stateName));

  useEffect(() => {
    const ids = new Set(tableData.map((entry) => mapId(entry)));
    const deletedChecked = Object.keys(checked).filter((id) => !ids.has(id));
    const deletedCollapsible = Object.keys(collapsible).filter((id) => !ids.has(id));
    const deleted = [...new Set([...deletedChecked, ...deletedCollapsible])];
    if (deleted.length > 0) {
      store.dispatch(removeDeleted({ name: stateName, ids: deleted }));
    }
  }, [tableData, checked, collapsible, stateName, mapId]);
  return <>{null}</>;
};

const DynamicTableInner = <T,>(props: DynamicTableProps<T>, ref: React.Ref<HTMLDivElement | null>) => {
  const { id: selectedTab } = useParams();
  const basePath = useLocation().pathname.split("/")[1];
  const { tableData, totalNumEntities, columns, selectable = false, stateName } = props;
  const page = useSelector((state: RootState) => selectQueryPage(state, stateName));
  const perPage = useSelector((state: RootState) => selectQueryPerPage(state, stateName));

  const pages = Math.ceil(totalNumEntities / perPage);

  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const [tableContainerWidth, setTableContainerWidth] = useState(window.innerWidth);

  useImperativeHandle(ref, () => tableContainerRef.current, []);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      if (entries[0]) {
        setTableContainerWidth(entries[0].contentRect.width);
      }
    });

    if (tableContainerRef.current) {
      observer.observe(tableContainerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (page > pages) {
      store.dispatch(setPage({ name: stateName, value: pages }));
    } else if (pages !== 0 && page === 0) {
      store.dispatch(setPage({ name: stateName, value: 1 }));
    }
  }, [page, pages, stateName]);

  return (
    <>
      <DynamicTableUpdates stateName={stateName} mapId={props.mapId} tableData={tableData} />
      <Box
        component="div"
        sx={{
          minHeight: "72px",
          flexShrink: "1",
          overflow: "auto",
          position: "relative",
          ...(props.sx ?? {})
        }}
      >
        <Box
          component="div"
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            alignContent: "center",
            width: "100%",
            ...(props.sx ?? {})
          }}
        >
          {props?.menuOptions && (
            <Box
              component="div"
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: ["0.6em"],
                gap: "0.3em",
                width: "100%"
              }}
            >
              {props.menuOptions.map((option, index) => {
                return <TableMenuItem key={`option-${index}`} stateName={stateName} menu={option} />;
              })}
            </Box>
          )}
        </Box>

        <TableContainer
          component={Paper}
          elevation={0}
          ref={tableContainerRef}
          sx={{
            width: "100%",
            bgcolor: "inherit",
            maxHeight: tableData.length !== 0 ? (props.hidePagination ? "calc(100% - 0.6em)" : "calc(100% - 48px - 0.6em)") : undefined,
            minHeight: "60px"
          }}
        >
          <Table
            stickyHeader
            sx={{
              tableLayout: "fixed", // table is fixed for cell text ellipsis overflow
              // table width is max of 100% or the sum of the min-width of each column
              width: `max(100%, ${columns.reduce((sum, col) => sum + (col.minWidth ?? DEFAULT_MIN_COLUMN_WIDTH_PX), 0)}px)`
            }}
          >
            <Header
              stateName={stateName}
              checkable={props.checkable}
              columns={columns}
              tableData={tableData}
              mapId={props.mapId}
              onSort={props.onSort}
              tableContainerWidth={tableContainerWidth}
            />
            <TableBody sx={{ "&& > tr td": { userSelect: "none" } }}>
              {tableData.map((entry) => (
                <Row
                  key={`${props.mapId(entry)}`}
                  entry={entry}
                  mapId={props.mapId}
                  stateName={stateName}
                  renderCollapsible={props.renderCollapsible}
                  renderEntry={props.renderEntry}
                  selectable={selectable}
                  selectRows={constructSelectRangeOfRows({
                    stateName: props.stateName,
                    page: basePath,
                    tab: selectedTab,
                    mapId: props.mapId,
                    data: props.tableData
                  })}
                  columns={columns}
                  checkable={props.checkable}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {props.noMatch && <NoSearchResult />}

        {pages !== 0 && props.hidePagination !== true && (
          <Box component="div" sx={{ display: "flex", justifyContent: "center", marginTop: "1em" }}>
            <Pagination
              count={pages}
              page={page}
              onChange={(_, value) => {
                tableContainerRef.current?.scrollTo({ top: 0, left: 0 });
                store.dispatch(invalidate(stateName));
                store.dispatch(setPage({ name: stateName, value: value }));
              }}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

// Export the component with forwardRef and generic
export const DynamicTable = forwardRef(DynamicTableInner) as <T>(
  props: DynamicTableProps<T> & { ref?: React.Ref<HTMLDivElement | null> }
) => ReturnType<typeof DynamicTableInner>;

export type { Column, MenuOption, Sort };
export const DEFAULT_MIN_COLUMN_WIDTH_PX = 130;
